import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { MapPin, Calendar, ExternalLink, Tag, Building2, ThumbsUp, X } from 'lucide-react';
import { getEvents, getLocations } from '../api';
import { getApiErrorMessage } from '../utils/apiError';

function Events() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    location: '',
    hasOffer: false,
    clinic: searchParams.get('clinic') || '',
  });

  useEffect(() => {
    document.title = 'Events | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    loadLocations();
  }, []);

  useEffect(() => {
    const clinicParam = searchParams.get('clinic') || '';
    setFilters(prev => ({ ...prev, clinic: clinicParam, page: 1 }));
  }, [searchParams]);

  useEffect(() => {
    loadEvents();
  }, [filters]);

  const loadLocations = async () => {
    try {
      const response = await getLocations();
      setLocations(response.data.data);
    } catch (err) {
      console.error('Failed to load locations:', err);
    }
  };

  const loadEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getEvents(filters);
      setEvents(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load events'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold mb-2">
          {filters.clinic ? `${filters.clinic} Events` : 'Competitor Events'}
        </h1>
        <p className="text-sm sm:text-base text-gray-600">Browse all analyzed dental events from competitor ads</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-4 sm:items-end">
          <div className="flex-1 min-w-0 sm:min-w-[200px]">
            <label htmlFor="location-filter" className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              id="location-filter"
              className="input w-full"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value, page: 1 })}
            >
              <option value="">All Locations</option>
              {locations.map((location) => (
                <option key={location} value={location}>
                  {location}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.hasOffer}
                onChange={(e) => setFilters({ ...filters, hasOffer: e.target.checked, page: 1 })}
                className="rounded text-dental-600 focus:ring-dental-500"
              />
              <span className="text-sm text-gray-700">Has Special Offer</span>
            </label>
            <button
              type="button"
              onClick={loadEvents}
              disabled={loading}
              className="btn-primary whitespace-nowrap"
            >
              {loading ? 'Loading…' : 'Apply Filters'}
            </button>
          </div>
        </div>
        {filters.clinic && (
          <div className="mt-3 flex items-center gap-2">
            <span className="text-sm text-gray-600">Filtered by clinic:</span>
            <span className="inline-flex items-center gap-1 bg-dental-50 text-dental-700 border border-dental-200 rounded-full px-3 py-0.5 text-sm font-medium">
              {filters.clinic}
              <button
                onClick={() => {
                  setSearchParams({});
                  setFilters(prev => ({ ...prev, clinic: '', page: 1 }));
                }}
                className="ml-1 hover:text-dental-900"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="card bg-red-50 border border-red-200">
          <p className="text-red-700">{error}</p>
          <button onClick={() => { setError(null); loadEvents(); }} className="btn-secondary mt-2">
            Try again
          </button>
        </div>
      )}

      {/* Events Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dental-600"></div>
        </div>
      ) : events.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-gray-500 text-sm">No events found matching your criteria.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {events.map((event) => (
              <div key={event.id} className="card hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-base sm:text-lg mb-2 line-clamp-2">
                  {event.event_title || 'Untitled Event'}
                </h3>
                
                {event.offer && (
                  <div className="mb-3">
                    <span className="badge badge-success text-xs">{event.offer}</span>
                  </div>
                )}
                
                <div className="space-y-2 text-xs sm:text-sm text-gray-600">
                  {event.clinic_name && (
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 shrink-0" />
                      <Link
                        to={`/clinics/${encodeURIComponent(event.clinic_name)}`}
                        className="truncate hover:text-dental-600 hover:underline"
                      >
                        {event.clinic_name}
                      </Link>
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 shrink-0" />
                      <Link
                        to={`/locations/${encodeURIComponent(event.location)}`}
                        className="truncate hover:text-dental-600 hover:underline"
                      >
                        {event.location}
                      </Link>
                    </div>
                  )}
                  {(event.event_start_date || event.event_date) && (
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 shrink-0" />
                      <span className="truncate">
                        {event.event_start_date && event.event_end_date && event.event_start_date !== event.event_end_date
                          ? `${event.event_start_date} - ${event.event_end_date}`
                          : event.event_start_date || event.event_date}
                      </span>
                    </div>
                  )}
                  {event.treatment_types && event.treatment_types.length > 0 && (
                    <div className="flex items-start gap-2">
                      <Tag className="w-4 h-4 shrink-0 mt-0.5" />
                      <div className="flex flex-wrap gap-1">
                        {event.treatment_types.slice(0, 3).map(t => (
                          <span key={t} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-2">
                  <span className="text-xs text-gray-400">
                    Confidence: {((event.confidence_score || 0) * 100).toFixed(0)}%
                  </span>
                  {event.ad_count > 1 && (
                    <span className="text-xs bg-dental-100 text-dental-700 px-2 py-0.5 rounded-full">
                      {event.ad_count} ads
                    </span>
                  )}
                  {event.page_like_count > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ThumbsUp className="w-3 h-3" />
                      {event.page_like_count >= 1000 
                        ? `${(event.page_like_count / 1000).toFixed(1)}K` 
                        : event.page_like_count}
                    </span>
                  )}
                </div>
                  <div className="flex items-center gap-2 mt-2 sm:mt-0">
                    {event.RawAd?.ad_url && (
                      <a
                        href={event.RawAd.ad_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-xs sm:text-sm font-medium flex items-center gap-1"
                      >
                        View Ad <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                    <Link
                      to={`/events/${event.id}`}
                      className="text-dental-600 hover:text-dental-700 text-xs sm:text-sm font-medium flex items-center gap-1"
                    >
                      Details
                    </Link>
                  </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-6 sm:mt-8">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page === 1}
                className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-xs sm:text-sm whitespace-nowrap">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.totalPages}
                className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Events;
